var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useOrgLoyaltyTier } from "@iscr/cosmetics/loyalty/useOrgLoyaltyTier";
import { getPriceCoeff, getSpecPrices } from "@iscr/views/admin/prods/priceUtils";
import { ProductPriceCalculator } from "@iscr/views/prods/ProductPriceCalculator";
import { createContext, useContext, useMemo } from "react";
import { Loader } from "semantic-ui-react";
import { useLoyaltyTiers } from "./useLoyaltyTiers";
export const LoyaltyTierContext = createContext({
    loyaltyEnabled: false,
    tiers: undefined,
    currentUserOrgTier: undefined,
    calculator: undefined,
});
export const useLoyaltyContext = () => {
    const _a = useContext(LoyaltyTierContext), { tiers, currentUserOrgTier, calculator } = _a, rest = __rest(_a, ["tiers", "currentUserOrgTier", "calculator"]);
    // Tiers and calculator are guaranteed to be loaded by the LoyaltyTierProvider, these checks allow consumers to avoid having to check for undefined
    if (!tiers) {
        throw new Error("Loyalty tiers not loaded yet.");
    }
    if (!calculator) {
        throw new Error("Calculator not available yet.");
    }
    return Object.assign({ tiers, currentUserOrgTier, calculator }, rest);
};
// Load tiers in a context provider to avoid multiple loads and initialise product pricing calculator
export const LoyaltyTierProvider = ({ children }) => {
    var _a, _b;
    // TODO Once we can use react query, update this use react query's caching mechanism to avoid multiple API calls
    // from multiple mounted LoyaltyTierProviders
    const { data: tiers, loading: loadingTiers } = useLoyaltyTiers();
    const dvcOrg = ((_a = app.dvc) === null || _a === void 0 ? void 0 : _a.org) || undefined;
    const { data: currentUserOrgTier, loading: loadingCurrentUserOrgTier } = useOrgLoyaltyTier({
        // The user might be associated with multiple orgs (via Manage connected devices + org hierarchy),
        // so use the device org to determine their currently loyalty tier (and therefore pricing) if available
        oid: (_b = dvcOrg === null || dvcOrg === void 0 ? void 0 : dvcOrg.oid) !== null && _b !== void 0 ? _b : app.user.org.oid,
        year: new Date().getFullYear(),
    });
    const calculator = useMemo(() => {
        var _a;
        return tiers
            ? new ProductPriceCalculator({
                // If the user has no org loyalty tier, then they are not eligible for loyalty pricing
                loyaltyEnabled: !!(currentUserOrgTier === null || currentUserOrgTier === void 0 ? void 0 : currentUserOrgTier.loyaltyEnabled),
                specPrices: getSpecPrices(),
                priceCoeff: getPriceCoeff(),
                orgLoyaltyTierId: (_a = currentUserOrgTier === null || currentUserOrgTier === void 0 ? void 0 : currentUserOrgTier.tier) === null || _a === void 0 ? void 0 : _a.tier_id,
                loyaltyTiers: tiers,
            })
            : undefined;
    }, [currentUserOrgTier, tiers]);
    if (loadingTiers || loadingCurrentUserOrgTier) {
        return (_jsx("div", { "data-testid": "loader", children: _jsx(Loader, { active: true }) }));
    }
    return (_jsx(LoyaltyTierContext.Provider, { value: {
            tiers,
            currentUserOrgTier: currentUserOrgTier === null || currentUserOrgTier === void 0 ? void 0 : currentUserOrgTier.tier,
            calculator,
            loyaltyEnabled: !!(currentUserOrgTier === null || currentUserOrgTier === void 0 ? void 0 : currentUserOrgTier.loyaltyEnabled),
        }, children: children }));
};
